import { useMemo } from 'react';

import type { FormCountryAvailability } from '../../types';
import { SectionCard } from './SectionCard';

/**
 * Country availability — derived from the KB's "Core Model Intake Form
 * Enabled For" table. Lists every documented geography with its
 * enabled/disabled state, sorted enabled-first then alphabetically so a
 * reviewer can scan the active set at a glance.
 *
 * The header count shows `enabled / total` rather than the bare total —
 * a 6/47 form looks very different from a 47/47 one and the count is
 * cheap context. When the KB doesn't declare any geographies the section
 * collapses with the standard empty hint.
 */
export function CountryAvailability({
  countries,
}: {
  countries: FormCountryAvailability[];
}) {
  const sorted = useMemo(() => {
    return [...countries].sort((a, b) => {
      if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
      return a.country.localeCompare(b.country);
    });
  }, [countries]);

  const enabled = sorted.filter((c) => c.enabled).length;
  const total = sorted.length;

  return (
    <SectionCard
      title="Country availability"
      count={total > 0 ? total : 0}
      defaultOpen={enabled < total}
      emptyHint="No country availability documented in the KB."
      rightSlot={
        total > 0 ? (
          <span className="text-xs text-slate-500">
            {enabled}/{total} enabled
          </span>
        ) : null
      }
    >
      {total > 0 && (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5 text-sm">
          {sorted.map((c) => (
            <li
              key={c.country}
              className="flex items-center gap-2 min-w-0"
              title={c.enabled ? 'Enabled' : 'Not enabled'}
            >
              <span
                aria-hidden
                className={
                  'inline-block h-2 w-2 rounded-full shrink-0 ' +
                  (c.enabled ? 'bg-emerald-500' : 'bg-slate-300')
                }
              />
              <span
                className={
                  'truncate ' +
                  (c.enabled ? 'text-slate-800' : 'text-slate-400 line-through')
                }
              >
                {c.country}
              </span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
